import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from "tss-react/mui";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import withMediaQuery from 'components/WithMediaQuery';

import DialogActions from 'components/DialogActions';
import DialogTitle from 'components/DialogTitle';
import DialogTransition from 'components/DialogTransition';

import classnames from 'classnames';
import ProgressButton from 'components/ProgressButton';
import PrimaryDialogButton from 'components/PrimaryDialogButton';
import SecondaryDialogButton from 'components/SecondaryDialogButton';
import { Body1, H4 } from 'components/Typography';
import * as authActions from '../../actions';
import OutlineTextField from 'components/OutlineTextField';
import ResponsiveDialogHeader from 'components/ResponsiveDialogHeader';
import m from 'messages';

import styles from './styles';

class AuthenticationCodeDialog extends React.Component {

    constructor(props) {
        super(props);

        const { canSendEmail, canSendSms } = props;

        this.state = {
            sendEmail: canSendEmail && !canSendSms,
            sendSms: canSendSms && !canSendEmail,
            authCode: "",
            optionsDialogOpen: false
        };
    }

    handleClose = () => {
        this.props.dispatch(authActions.closeAuthCodeDialog());
    };

    handleCommunicationChange = (evt) => {
        this.setState({
            sendEmail: evt.target.value === "email",
            sendSms: evt.target.value === "sms"
        });
    };

    sendAuthCode = () => {
        let { sendSms, sendEmail } = this.state;
        this.props.sendAuthCode(sendSms, sendEmail);
    };

    openMyDialog = (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.setState({ optionsDialogOpen: true });

        return false;
    };

    handleCodeInput = (e) => {
        if (e.hasOwnProperty('keyCode') && e.keyCode === 13) {
            const authCodePresent = this.state.authCode && this.state.authCode.length > 0;
            if (authCodePresent) {
                this.submit();
            }
        }

        this.setState({ authCode: e.target.value });
    };

    submit = () => {
        this.props.confirmClicked(this.state.authCode);
        this.reset();
    };

    reset = () => {
        this.setState({
            sendEmail: false,
            sendSms: false,
            authCode: ""
        });
    };

    authCodeDialogClose(handleClose) {
        return (_event, reason) => {
            if (reason === 'backdropClick')
                return;
            handleClose();
        };
    }

    render() {

        let {
            classes,
            authCodeDialogOpen,
            codeSending,
            canSendEmail,
            canSendSms,
            myProductName,
            baseTitle,
            handleClose,
            fullScreen
        } = this.props;

        let {
            sendEmail,
            sendSms,
            authCode,
            optionsDialogOpen
        } = this.state;

        const authCodePresent = authCode && authCode.length > 0 && authCode <= 2147483647;

        return (
            <div>
                <Dialog
                    open={optionsDialogOpen === true}
                    onClose={() => this.setState({ optionsDialogOpen: false })}
                    TransitionComponent={DialogTransition}
                    aria-labelledby="info-dialog-title"
                    aria-describedby="info-dialog-description">

                    <DialogTitle id="info-dialog-title">
                        {m.auth.preferredOptionUnavailable}
                    </DialogTitle>

                    <DialogContent id="info-dialog-description">
                        <Body1 paragraph>
                            {m.auth.noContactDetails}
                        </Body1>
                        <Body1 paragraph>
                            {m.auth.useProductLink(myProductName)}
                        </Body1>
                    </DialogContent>
                    <DialogActions className={classes.actionRoot}>
                        <SecondaryDialogButton onClick={() => this.setState({ optionsDialogOpen: false })}>
                            {m.general.cancel}
                        </SecondaryDialogButton>
                        <PrimaryDialogButton style={{ textTransform: 'none' }} component={Link} rel="noreferrer" href={`${this.props.config.baseURL}/MySkillGuard`} target="_blank">
                            {myProductName}
                        </PrimaryDialogButton>
                    </DialogActions>
                </Dialog>

                <Dialog
                    fullScreen={fullScreen}
                    open={!!authCodeDialogOpen}
                    onClose={this.authCodeDialogClose(handleClose)}
                    TransitionComponent={DialogTransition}
                    aria-labelledby="auth-dialog-title"
                    aria-describedby="auth-dialog-description">

                    <ResponsiveDialogHeader
                        handleClose={this.handleClose}
                        fullScreen={fullScreen}
                        fullScreenTitle={baseTitle}
                        title="Cardholder authentication" />

                    <DialogContent id="auth-dialog-description" className={classes.content}>
                        <Body1 paragraph>
                            <span dangerouslySetInnerHTML={m.auth.sendAuthCodeMessage}></span>
                        </Body1>
                        <Body1>
                            {m.auth.selectMessageOption}
                        </Body1>
                        <Grid container spacing={1} className={classes.gutter} alignItems="flex-start">
                            <Grid item xs={12}>
                                <FormGroup row>
                                    <RadioGroup
                                        row
                                        className={classes.radioGroup}
                                        name="sendOptions"
                                        onChange={this.handleCommunicationChange}>
                                        <FormControlLabel
                                            checked={sendEmail}
                                            value="email"
                                            control={<Radio />}
                                            label={m.auth.emailLabel}
                                            disabled={!canSendEmail} />
                                        <FormControlLabel
                                            checked={sendSms}
                                            value="sms"
                                            control={<Radio />}
                                            label={m.auth.smsLabel}
                                            disabled={!canSendSms} />
                                    </RadioGroup>
                                </FormGroup>
                                <Link
                                    onClick={this.openMyDialog}
                                    href="/"
                                    className={classes.textLink}
                                    underline="hover">
                                    {m.auth.preferredOptionUnavailable}
                                </Link>
                            </Grid>
                            <Grid item xs={12}>
                                <div>
                                    <ProgressButton
                                        active={codeSending}
                                        disabled={!sendEmail && !sendSms}
                                        onClick={this.sendAuthCode}
                                        text={m.auth.sendText} />
                                </div>
                            </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <H4>
                                    {m.auth.authCodeHeader}
                                </H4>
                                <Body1>
                                    {m.auth.enterCodeBelow}
                                </Body1>
                            </Grid>
                            <Grid item xs={12}>
                                <OutlineTextField
                                    placeholder={m.auth.authCodePlaceholder}
                                    className={classnames(classes.codeField)}
                                    type="number"
                                    value={this.state.authCode}
                                    onKeyUp={this.handleCodeInput}
                                    onChange={this.handleCodeInput}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <PrimaryDialogButton
                                    onClick={this.submit}
                                    disabled={!authCodePresent}>
                                    {m.auth.authenticateButton}
                                </PrimaryDialogButton>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    {!fullScreen &&
                        <DialogActions>
                            <SecondaryDialogButton onClick={this.props.handleClose}>
                                {m.general.cancel}
                            </SecondaryDialogButton>
                        </DialogActions>
                    }
                </Dialog>
            </div >
        );
    }
}

function mapStateToProps(state) {

    const { auth, config } = state;

    return {
        canSendSms: auth.smsAvailable,
        canSendEmail: auth.emailAvailable,
        myProductName: config.myProductName,
        authCodeDialogOpen: auth.authCodeDialogOpen,
        config: config,
        codeSending: auth.codeSending,
        baseTitle: config.appName
    };
}

AuthenticationCodeDialog.propTypes = {
    handleClose: PropTypes.func.isRequired,
    updateDetails: PropTypes.func.isRequired,
    sendAuthCode: PropTypes.func.isRequired,
    confirmClicked: PropTypes.func.isRequired
};

const Media = withMediaQuery(theme => theme.breakpoints.down('lg'))(AuthenticationCodeDialog);
const styledDialog = withStyles(Media,styles);
const connectedDialog = connect(mapStateToProps)(styledDialog);

export default connectedDialog;